import type { CognitoUserSession } from "amazon-cognito-identity-js";
import { config } from "./config";

export interface DropJetFile {
  fileId: string;
  originalName: string;
  contentType: string;
  size: number;
  status: "PENDING" | "AVAILABLE" | "REVOKED";
  createdAt: string;
  expiresAt: number;
  downloads: number;
  maxDownloads?: number;
}

interface CreateUploadResponse {
  file: DropJetFile;
  shareToken: string;
  upload: { method: "PUT"; url: string; headers: Record<string, string>; expiresInSeconds: number };
}

export async function uploadFile(
  session: CognitoUserSession,
  file: File,
  expiresInHours: number,
  maxDownloads?: number,
): Promise<{ file: DropJetFile; shareToken: string }> {
  const reservation = await api<CreateUploadResponse>(
    "/uploads",
    {
      method: "POST",
      body: JSON.stringify({
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        size: file.size,
        expiresInHours,
        ...(maxDownloads ? { maxDownloads } : {}),
      }),
    },
    session,
  );

  const uploadResponse = await fetch(reservation.upload.url, {
    method: "PUT",
    headers: reservation.upload.headers,
    body: file,
  });
  if (!uploadResponse.ok) throw new Error("S3 rejected the file upload");

  await api(`/uploads/${reservation.file.fileId}/complete`, { method: "POST" }, session);
  return {
    file: { ...reservation.file, status: "AVAILABLE" },
    shareToken: reservation.shareToken,
  };
}

export async function listFiles(session: CognitoUserSession): Promise<DropJetFile[]> {
  return (await api<{ files: DropJetFile[] }>("/uploads", {}, session)).files;
}

export async function revokeFile(session: CognitoUserSession, fileId: string): Promise<void> {
  await api(`/uploads/${fileId}`, { method: "DELETE" }, session);
}

export async function resolveShare(
  fileId: string,
  token: string,
): Promise<{ file: DropJetFile; downloadUrl: string; expiresInSeconds: number }> {
  return await api(`/shares/${fileId}/resolve`, {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

async function api<T>(
  path: string,
  init: RequestInit = {},
  session?: CognitoUserSession,
): Promise<T> {
  const response = await fetch(`${config().apiUrl}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(session ? { authorization: session.getIdToken().getJwtToken() } : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) return undefined as T;
  const body = (await response.json()) as { error?: { message?: string } } & T;
  if (!response.ok) throw new Error(body.error?.message ?? `Request failed with status ${response.status}`);
  return body;
}
